import type {
  ConformityBlockNode,
  ConformityInlineNode,
} from "../../../../api/conformityApi"

interface TiptapDocLike {
  content?: unknown[]
}

/** Every `paragraph` node's slot -> its inline content, read off a Tiptap
 *  doc snapshot (`editor.getJSON()` shape). Used to find which slot(s)
 *  actually changed after an edit — never `selection.$from.parent`, which
 *  stops meaning "the edited paragraph" the moment a transaction has split,
 *  merged, or moved nodes around (exactly what a paste or an Enter used to
 *  do before SlotParagraph's keymap fix; diffing the whole doc is correct
 *  regardless of what future edits do to the tree shape). */
export function paragraphSlotsOf(
  doc: TiptapDocLike,
): Map<string, ConformityInlineNode[]> {
  const slots = new Map<string, ConformityInlineNode[]>()
  for (const node of (doc.content ?? []) as ConformityBlockNode[]) {
    if (node.type === "paragraph" && typeof node.attrs.slot === "string") {
      slots.set(node.attrs.slot, node.content ?? [])
    }
  }
  return slots
}

/** Merge an incoming (server-refreshed) doc with the live editor's current
 *  content, keeping the LIVE value for any slot still `dirty` (edited but
 *  not yet confirmed saved) instead of the incoming one. Applying a server
 *  refresh — a left-panel recompute, or the response of another slot's own
 *  autosave — must never discard an in-progress edit to a slot that simply
 *  hasn't reached the debounce yet; this is what makes that safe regardless
 *  of when the refresh lands relative to typing. System fragments always
 *  come from `incoming` — they're never user content. A dirty slot the
 *  incoming tree no longer offers (its framework got deselected) is simply
 *  dropped, same as any other removed slot. */
export function mergeExternalContent(
  incoming: TiptapDocLike,
  current: TiptapDocLike,
  dirtySlots: ReadonlySet<string>,
): TiptapDocLike {
  if (dirtySlots.size === 0) return incoming
  const currentSlots = paragraphSlotsOf(current)
  return {
    ...incoming,
    content: (incoming.content ?? []).map((node) => {
      const block = node as ConformityBlockNode
      if (
        block.type === "paragraph" &&
        dirtySlots.has(block.attrs.slot) &&
        currentSlots.has(block.attrs.slot)
      ) {
        return { ...block, content: currentSlots.get(block.attrs.slot)! }
      }
      return node
    }),
  }
}

/** The slots whose content differs between two doc snapshots — a slot
 *  present in `after` but changed (or newly present) relative to `before`.
 *  A slot that only exists in `before` (e.g. a framework got deselected,
 *  dropping its paragraph) is not "changed" here — there is nothing left to
 *  save for it. */
export function changedParagraphSlots(
  before: TiptapDocLike,
  after: TiptapDocLike,
): string[] {
  const beforeSlots = paragraphSlotsOf(before)
  const afterSlots = paragraphSlotsOf(after)
  const changed: string[] = []
  for (const [slot, content] of afterSlots) {
    const prior = beforeSlots.get(slot)
    if (!prior || JSON.stringify(prior) !== JSON.stringify(content)) {
      changed.push(slot)
    }
  }
  return changed
}
